import { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import api from '../api/axios';

// ── Hard-coded fallback — shown immediately if API is unreachable ───
const PRICES_FALLBACK = [
  { destination: 'Stone Town', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:15 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:25 },
  ]},
  { destination: 'Nungwi', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:50 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Paje', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:45 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Kizimkazi', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:45 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Fumba Town', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:40 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Makunduchi', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:50 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Michamvi', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:50 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Jambiani', vehicles: [
    { id:0, vehicle_type:'alphard', vehicle_label:'Toyota Alphard', max_passengers:5,  price_usd:45 },
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
  { destination: 'Bwejuu', vehicles: [
    { id:0, vehicle_type:'minivan', vehicle_label:'Mini Van',        max_passengers:10, price_usd:65 },
  ]},
];

// ── App constants ──────────────────────────────────────────────────
const CRDB_ACCOUNT  = process.env.REACT_APP_CRDB_ACCOUNT  || '0152000GY0M00';
const NMB_ACCOUNT   = process.env.REACT_APP_NMB_ACCOUNT   || '23410065484';
const BANK_HOLDER   = process.env.REACT_APP_BANK_HOLDER   || 'ABUBAKAR SAID SHEE';
const MPESA_TILL    = process.env.REACT_APP_MPESA_TILL_NUMBER || '255779987604';
const WHATSAPP_NUM     = '255779987604';
const WHATSAPP_DISPLAY = '+255 779 987 604';
const AIRPORT_LABEL    = 'Zanzibar International Airport (ZNZ)';

const VEHICLE_IMAGES = {
  alphard: `${process.env.PUBLIC_URL}/alphard.jpeg`,
  minivan: `${process.env.PUBLIC_URL}/minivan.jpeg`,
};

const HOTELS_BY_ZONE = {
  'Stone Town':  ['Park Hyatt Zanzibar','Zanzibar Serena Hotel','Emerson Spice Hotel','Tembo House Hotel','236 Hurumzi','Zanzibar Palace Hotel','Jafferji House & Spa'],
  'Nungwi':      ['Essque Zalu Zanzibar','Zawadi Hotel','DoubleTree by Hilton Nungwi','Ras Nungwi Beach Hotel',"La Gemma dell'Est",'Amaan Bungalows','Bahari Beach Hotel','Z Hotel Zanzibar'],
  'Paje':        ['Baraza Resort & Spa','White Sand Luxury Villas','Hakuna Majiwe Lodge','Butiama Beach Hotel','Paje by Night','Crystal Bay Beach Resort','Shooting Star Lodge'],
  'Kizimkazi':   ['Swahili Beach Resort','Waterlovers Beach Resort','Casa del Mar Zanzibar','Unguja Lodge','Kizimkazi Beach Club'],
  'Fumba Town':  ['Manta Resort','Fumba Beach Lodge','Chumbe Island Coral Park','The Residence Zanzibar'],
  'Makunduchi':  ['Zanzibar White Sand Luxury Villas','Sunshine Hotel Makunduchi'],
  'Michamvi':    ['Sultan Sands Island Resort','Michamvi Sunset Bay Hotel','Kichanga Lodge','Zanzibello Hotel','Reef & Beach Resort'],
  'Jambiani':    ['Karafuu Beach Resort & Spa','Bluebay Beach Resort','Jambiani Beach Hotel','Shamba Lodge','Red Monkey Beach Lodge','Villa Kiva'],
  'Bwejuu':      ["Breezes Beach Club & Spa","Palumbo Reef Hotel","Karafuu Hotel Beach Resort","Robinson's Place",'Evergreen Bungalows'],
};

const ZANZIBAR_ACTIVITIES = [
  {
    title:'Stone Town Historical Tour',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/Forodhani.jpg?width=400',
    desc:'Explore the UNESCO World Heritage old town.',
    tag:'History & Culture',
    article:[
      'Stone Town is the historic heart of Zanzibar City and a UNESCO World Heritage Site since 2000. Its labyrinthine streets are lined with Arab, Persian, Indian and European architecture that reflects centuries of trade and cultural exchange on the Swahili Coast.',
      'Wander past the iconic House of Wonders — the first building in East Africa to have electricity and an elevator — the ancient Arab Fort built in the 17th century, and the ornate carved wooden doors that are a hallmark of Stone Town craftsmanship. Each door tells a story: brass studs repelled war elephants, and the height of the frame reflected the owner\'s wealth.',
      'As evening falls, the Forodhani Gardens night market comes alive with grills, Zanzibar pizza, freshly caught seafood and sugar-cane juice. A guided walk through Stone Town takes 2–3 hours; combine it with a sunset transfer from the airport for the perfect first impression of the island.',
    ],
  },
  {
    title:'Spice Farm Tour',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/2024-06-15%20Zanzibar%20spices%200460.JPG?width=400',
    desc:"Discover Zanzibar's famous spice plantations.",
    tag:'Nature & Food',
    article:[
      'Zanzibar earned the nickname "Spice Island" for good reason. Cloves, vanilla, cinnamon, cardamom, black pepper and nutmeg have been cultivated here for centuries, making this small island one of the world\'s most important spice producers. At its peak, Zanzibar supplied 90% of the world\'s cloves.',
      'A spice farm tour takes you deep into lush plantations where a knowledgeable guide challenges you to identify each plant by sight and smell before revealing its name. You\'ll taste fresh coconut, see the brilliant red of a clove bud and learn how vanilla is hand-pollinated. Many tours end with a traditional Swahili lunch cooked entirely with spices grown on the farm.',
      'Most spice farms are located in the Kizimbani and Kindichi areas, about 30 minutes from Stone Town. A half-day tour pairs perfectly with a morning airport transfer — ask our team to arrange a seamless connection.',
    ],
  },
  {
    title:'Snorkeling Mnemba',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/Mnemba%20Atoll%20-%2051861330922.jpg?width=400',
    desc:'Crystal clear waters and vibrant coral reefs.',
    tag:'Ocean & Marine Life',
    article:[
      'The Mnemba Atoll Marine Conservation Area, just off the northeast tip of Zanzibar, is one of the finest snorkeling and diving destinations in the Indian Ocean. The protected waters are home to over 400 species of fish, vibrant hard and soft corals, sea turtles, spinner dolphins and seasonal humpback whales.',
      'Visibility regularly exceeds 20 metres in the calm, warm water (average 26°C). Even first-time snorkelers find themselves face-to-face with hawksbill turtles grazing on coral, parrotfish nibbling at reef, and clouds of sergeant major fish drifting in formation around the bommies.',
      'Day trips depart from Nungwi and Matemwe beaches by speedboat. The ideal season is June to October when currents are calm and visibility peaks. Neruda Tours can arrange a seamless transfer from your hotel or the airport directly to the departure beach — just ask via WhatsApp.',
    ],
  },
  {
    title:'Sunset Dhow Cruise',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/Dhow%20Sunset%2C%20Zanzibar%20%2810164046475%29.jpg?width=400',
    desc:'Sail the Indian Ocean at sunset.',
    tag:'Romance & Leisure',
    article:[
      'Few experiences rival watching the sun sink into the Indian Ocean from the deck of a traditional Zanzibar dhow. These handcrafted wooden sailing vessels have plied these waters for over a thousand years, trading between Africa, Arabia and India. Today they carry travellers on one of the most memorable journeys Zanzibar can offer.',
      'Sunset cruises typically depart from Stone Town harbour at 4 pm, sailing north along the coast with the sea breeze filling the triangular lateen sail. Guests are served fresh tropical fruit, Zanzibar spiced tea and cold drinks as the sky transforms from gold to deep crimson and the call to prayer drifts out from the ancient minarets of the old town.',
      'Private dinner dhows are also available — tables set on deck with candles, a fresh seafood barbecue and stargazing on the return journey. Contact us on WhatsApp to arrange a transfer that times perfectly with your cruise departure.',
    ],
  },
  {
    title:'Prison Island',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/Aldabra%20giant%20tortoises%20on%20Changuu.jpg?width=400',
    desc:'Visit the famous giant tortoise sanctuary.',
    tag:'Wildlife & History',
    article:[
      'Changuu Island — better known as Prison Island — lies just 5 km northwest of Stone Town in turquoise, glass-flat water. Built as a detention centre in the 1860s but never used as a prison, it later became a quarantine station for ships arriving on the Swahili Coast. Today it is best known for its remarkable population of Aldabra giant tortoises, gifted from Seychelles in 1919.',
      'Some of these tortoises are over 100 years old and have grown to weigh more than 250 kg. Visitors can feed them leaves and pose for photographs beside creatures that were alive during World War I. The island also has some of the best snorkeling close to Stone Town, with healthy coral gardens and colourful reef fish just steps from the beach.',
      'The island is reached by a 20-minute boat ride from the stone jetty in Stone Town. Combine a morning visit with an afternoon Stone Town walking tour and a Neruda Tours evening transfer for a perfect full day on the island.',
    ],
  },
  {
    title:'Nakupenda Island',
    img:`${process.env.PUBLIC_URL}/nakupenda-island.jpg`,
    desc:'White sandbank, turquoise water and a relaxed island picnic.',
    tag:'Beach & Snorkeling',
    article:[
      'Nakupenda Island is a bright white sandbank just off Stone Town, surrounded by shallow turquoise water that is perfect for swimming, relaxing and taking photos.',
      'A typical visit combines a dhow or boat ride, time on the sandbank, snorkeling around the clear shallows and a beach picnic with fresh seafood, tropical fruit and cold drinks.',
      'Nakupenda pairs naturally with Prison Island because both are close to Stone Town. Neruda Tours can connect your airport or hotel transfer with the boat departure so the day feels easy from pickup to return.',
    ],
  },
  {
    title:'Salaam Cave',
    img:`${process.env.PUBLIC_URL}/salaam-cave-turtles.jpg`,
    desc:'Swim and relax with sea turtles in clear cave water.',
    tag:'Turtles & Nature',
    article:[
      'Salaam Cave is known for its calm turquoise water and turtle experience, where guests can swim, float and take photos close to these gentle sea creatures in a natural cave setting.',
      'It is a relaxed stop for families, couples and small groups who want something different from the beach: clear water, shaded cave walls and an easy nature encounter without needing a full-day boat trip.',
      'Salaam Cave is an easy half-day excursion from the southeast coast resorts around Paje, Jambiani and Kizimkazi. Neruda Tours can route your hotel-to-airport transfer through the area — ask via WhatsApp and we will arrange a turtle cave visit that fits perfectly around your departure time.',
    ],
  },
  {
    title:'Jozani Forest',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/Zanzibar%20Red%20Colobus%20Monkey.jpg?width=400',
    desc:'Home of the rare red colobus monkey.',
    tag:'Eco & Wildlife',
    article:[
      'Jozani–Chwaka Bay National Park is Zanzibar\'s only national park and the last significant fragment of ancient groundwater forest left on the island. Its 50 square kilometres shelter an extraordinary range of wildlife found nowhere else on Earth, most famously the Kirk\'s red colobus — an endangered monkey species endemic to Zanzibar with a population of fewer than 4,000 individuals.',
      'Guided walks through the dappled forest canopy bring you within metres of family groups of red colobus as they leap through the mahogany and fig trees above. The forest also shelters Aders\' duiker, Zanzibar leopard (critically rare), 50+ species of birds and hundreds of butterfly species. A mangrove boardwalk at the forest edge winds through a tidal ecosystem of extraordinary richness.',
      'Jozani is located about 35 km southeast of Stone Town, making it an ideal stop between the airport and the south-coast beach resorts of Paje, Jambiani or Kizimkazi. Ask Neruda Tours to route your transfer through the park — a two-hour guided walk costs around $10 and is unforgettable.',
    ],
  },
];

const TANZANIA_SAFARIS = [
  {
    title:'Serengeti National Park',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/019%20Sunrise%20in%20the%20Serengeti%20National%20Park%20Photo%20by%20Giles%20Laurent.jpg?width=400',
    desc:'Witness the great wildebeest migration.',
    tag:'Great Migration',
    article:[
      'The Serengeti is one of the Seven Natural Wonders of Africa — 30,000 square kilometres of open savanna, acacia woodland and rocky kopjes stretching to the horizon. It is home to the largest terrestrial mammal migration on Earth: each year, 1.5 million wildebeest and 300,000 zebra circle the ecosystem in an endless clockwise loop following the rains.',
      'The drama peaks at the Mara River crossings between July and September, where massive herds plunge into crocodile-filled water in a thundering, chaotic spectacle that has no equal in the natural world. But the Serengeti is extraordinary year-round — January and February bring newborn wildebeest calves and cheetah hunts on the short-grass plains of the south, while resident lion prides, elephant herds and leopards resting in acacia trees are visible on every game drive.',
      'The Serengeti is reached via a 45-minute flight from Zanzibar to Kilimanjaro Airport or Arusha, followed by a light aircraft or road transfer into the park. Neruda Tours arranges seamless Zanzibar airport transfers to connect with your mainland safari flights — contact us on WhatsApp for a tailored itinerary.',
    ],
  },
  {
    title:'Mikumi National Park',
    img:`${process.env.PUBLIC_URL}/mikumi-national-park.jpg`,
    desc:"Tanzania's most accessible wilderness escape.",
    tag:'Wildlife Safari',
    article:[
      'Mikumi National Park is Tanzania\'s fourth-largest national park, covering 3,230 square kilometres of open floodplain, miombo woodland and acacia savanna in central Tanzania. Often called the "Little Serengeti," the Mkata Floodplain at the heart of the park provides a sweeping, horizon-wide stage for some of East Africa\'s most spectacular wildlife viewing — and it is easily reached just 4 hours by road from Dar es Salaam.',
      'The park\'s grasslands support large herds of elephant, buffalo, zebra and wildebeest, while lion prides are regularly spotted on the floodplain and leopards haul kills into the fever trees overnight. Mikumi is also famous for its hippo pools, where pods of up to 30 hippos rest in the shallows of the Mkata River just metres from game-drive vehicles, bellowing and jostling in a spectacle that never grows old.',
      'Because of its proximity to the coast, Mikumi pairs perfectly with a Zanzibar beach stay — drive or fly in from Dar es Salaam, spend two nights on safari, then connect with Neruda Tours for a seamless airport transfer on the island. Contact us on WhatsApp to design your ideal Tanzania circuit.',
    ],
  },
  {
    title:'Selous (Nyerere) National Park',
    img:`${process.env.PUBLIC_URL}/selous-rufiji-river.jpg`,
    desc:"Africa's largest protected wilderness.",
    tag:'Wild & Remote',
    article:[
      'The Selous Game Reserve — renamed Nyerere National Park in 2019 in honour of Tanzania\'s founding president — is one of the largest protected areas on Earth, covering over 54,600 square kilometres of remote wilderness in southern Tanzania. A UNESCO World Heritage Site, it shelters one of Africa\'s greatest concentrations of elephant, hippo, crocodile and wild dog in a landscape almost entirely free of crowds.',
      'Unlike the northern circuit parks, Nyerere is famous for its boat safaris along the vast Rufiji River, where hippos surface between dugout canoes, Nile crocodiles bask on sandbanks and fish eagles call from ancient trees overhead. Walking safaris led by armed rangers bring you face-to-face with the ecosystem at ground level — reading tracks, reading the wind and experiencing the African bush as our ancestors did.',
      'The park is best accessed by a 30-minute charter flight from Dar es Salaam. Combine a Nyerere fly-in safari with arrival or departure through Zanzibar — Neruda Tours handles all island airport transfers, making the connection between beach and bush completely seamless.',
    ],
  },
  {
    title:'Tarangire National Park',
    img:'https://commons.wikimedia.org/wiki/Special:FilePath/Elephants%2C%20Tarangire%20National%20Park%20%285%29%20%2828671103666%29.jpg?width=400',
    desc:'Famous for massive elephant herds.',
    tag:'Elephants & Baobabs',
    article:[
      'Tarangire National Park is Tanzania\'s best-kept safari secret — a landscape of ancient baobab trees, swaying elephant grass and the Tarangire River that acts as a magnet for wildlife during the dry season (June–October). During this period Tarangire hosts one of the highest concentrations of elephants on Earth, with herds of several hundred animals converging on the riverbanks, their red dust-caked skin contrasting magnificently against the ochre soil.',
      'The park\'s 2,850 square kilometres are also home to large numbers of buffalo, lion, leopard, oryx, fringe-eared oryx and the stately gerenuk. Over 550 bird species have been recorded here, making it a paradise for birders — the yellow-collared lovebird and the Masai ostrich are particular highlights. The iconic baobab trees, some over 1,000 years old and with trunks wide enough for a family to shelter inside, give the landscape a prehistoric, almost surreal quality.',
      'Tarangire is 2 hours by road from Arusha and makes an excellent addition to a Serengeti–Ngorongoro circuit. Neruda Tours connects all your Zanzibar transfers at the start and end of your Tanzania adventure — contact us on WhatsApp to plan your journey.',
    ],
  },
];

const TESTIMONIALS = [
  { name:'Harris',           stars:5, text:'It was an incredibly easy process with step by step guide for airport collection. Our driver was very understanding and helpful.' },
  { name:'Carlos Bazan',     stars:5, text:'After changes in flight arrival times our driver was waiting for us. Very great professionalism, I highly recommend them.' },
  { name:'Maria Schmidt',    stars:5, text:'Excellent service from booking to drop off. The Alphard was spotless and the driver very professional. Will book again.' },
  { name:'James Okonkwo',    stars:5, text:'Booked a minivan for our family of 8. The driver arrived early, helped with all our luggage and made us feel very welcome. Fantastic service!' },
  { name:'Sophie Lefebvre',  stars:5, text:'I was nervous about a first visit to Zanzibar but the transfer was seamless. Our driver was friendly and gave us great local tips on the way.' },
  { name:'Tariq Al-Rashidi', stars:5, text:'Punctual, clean vehicle and a very polite driver. They waited for our delayed flight without any extra charge. Highly recommended for airport transfers.' },
  { name:'Amelia Torres',    stars:5, text:'We used Neruda Tours for both our arrival and departure transfers. Both times flawless. Booking was easy and the price was very fair for the quality.' },
];

const FAQS = [
  { q:'How long does booking take?',          a:'2 to 3 minutes — fill the form and receive instant confirmation by email.' },
  { q:'What payment methods do you accept?',  a:'Bank Transfer (CRDB Bank), M-Pesa / Tigo Pesa / Airtel Money, and Cash on Arrival.' },
  { q:'Can I cancel my booking?',             a:'Yes, with 24 hours notice for a full refund. Contact us on WhatsApp.' },
  { q:'Do you operate 24 hours?',             a:'Yes — we are available 24 hours, 7 days a week for all transfers.' },
];

const TICKER = [
  'Stone Town','Nungwi','Paje','Kizimkazi','Fumba Town','Makunduchi','Michamvi','Jambiani','Bwejuu',
  'Stone Town','Nungwi','Paje','Kizimkazi','Fumba Town','Makunduchi','Michamvi','Jambiani','Bwejuu',
];

// ── Hooks ──────────────────────────────────────────────────────────
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

function useCounter(target, ms = 1400, active = false) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let v = 0;
    const step = target / (ms / 16);
    const t = setInterval(() => {
      v += step;
      if (v >= target) { setN(target); clearInterval(t); } else setN(Math.floor(v));
    }, 16);
    return () => clearInterval(t);
  }, [active, target, ms]);
  return n;
}

// ── Small reusable pieces ──────────────────────────────────────────
function Stars({ n }) {
  return (
    <div className="flex gap-0.5 mb-3">
      {Array.from({ length: n }).map((_, i) => (
        <svg key={i} className="w-4 h-4 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

const WA_ICON = (
  <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// ── Stat strip with animated counters ─────────────────────────────
function TransferRouteAnimation({ variant = 'hero' }) {
  return (
    <div className={`transfer-route transfer-route--${variant}`} aria-hidden="true">
      <div className="transfer-route__label transfer-route__label--start">ZNZ Airport</div>
      <div className="transfer-route__label transfer-route__label--end">Hotel</div>
      <svg className="transfer-route__map" viewBox="0 0 680 240" fill="none">
        <path
          className="transfer-route__road"
          d="M68 166 C170 76 276 210 374 120 C452 48 536 86 614 58"
        />
        <path
          className="transfer-route__glow"
          d="M68 166 C170 76 276 210 374 120 C452 48 536 86 614 58"
        />
      </svg>
      <div className="transfer-route__pin transfer-route__pin--start">
        <span />
      </div>
      <div className="transfer-route__pin transfer-route__pin--end">
        <span />
      </div>
      <div className="transfer-route__vehicle">
        <svg viewBox="0 0 64 36" fill="none">
          <path d="M12 23h3l4-10c1-2 3-3 5-3h16c3 0 5 1 7 4l5 9h3c2 0 4 2 4 4v2H8v-2c0-2 2-4 4-4z" fill="currentColor" />
          <path d="M23 13h8v9H19l3-7c0-1 1-2 1-2zm11 0h6c2 0 3 1 4 2l4 7H34v-9z" fill="#F5F0E8" opacity=".85" />
          <circle cx="20" cy="30" r="5" fill="#1A3A2A" />
          <circle cx="20" cy="30" r="2" fill="#C4956A" />
          <circle cx="47" cy="30" r="5" fill="#1A3A2A" />
          <circle cx="47" cy="30" r="2" fill="#C4956A" />
        </svg>
      </div>
    </div>
  );
}

function StatStrip() {
  const [ref, vis] = useReveal(0.3);
  const years = useCounter(3,    1200, vis);
  const trips  = useCounter(2000, 1600, vis);
  const dests  = useCounter(9,   1000, vis);
  return (
    <div ref={ref} className="bg-brand-green border-y border-brand-ocean/40 py-6 px-6">
      <div className="max-w-3xl mx-auto grid grid-cols-3 gap-4 text-center">
        {[
          { val:`${years}+`,  label:'Years of Service' },
          { val: trips >= 1000 ? `${Math.floor(trips/1000)}K+` : `${trips}+`, label:'Happy Travellers' },
          { val:`${dests}`,   label:'Destinations' },
        ].map(({ val, label }) => (
          <div key={label}>
            <p className="font-serif text-3xl font-bold text-brand-gold">{val}</p>
            <p className="text-brand-sand text-xs tracking-wide mt-0.5">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── FAQ item ───────────────────────────────────────────────────────
function FaqItem({ q, a, open, onToggle }) {
  return (
    <div className="border border-brand-sand rounded-xl overflow-hidden">
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-brand-bg/60 transition-colors">
        <span className="font-semibold text-brand-green text-sm pr-4">{q}</span>
        <svg className={`w-5 h-5 text-brand-gold shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className={`faq-answer ${open ? 'open' : ''} bg-white border-t border-brand-sand/50`}>
        <p className="px-5 py-4 text-brand-ocean text-sm leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

// ── Attraction article modal ───────────────────────────────────────
function AttractionModal({ attraction, onClose }) {
  if (!attraction) return null;
  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto animate-modal-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Image header */}
        <div className="relative h-56 sm:h-64 overflow-hidden rounded-t-3xl sm:rounded-t-2xl shrink-0">
          <img
            src={attraction.img}
            alt={attraction.title}
            width="600"
            height="256"
            loading="lazy"
            style={{ backgroundColor: '#1A3A2A', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.opacity = '0'; }}
            className="w-full h-full object-cover card-img"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white text-lg font-bold transition-colors"
          >
            &times;
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-gold bg-brand-green/80 rounded-full px-3 py-1">
              {attraction.tag}
            </span>
            <h2 className="font-serif text-2xl font-bold text-white mt-2 leading-tight">
              {attraction.title}
            </h2>
          </div>
        </div>

        {/* Article body */}
        <div className="px-6 py-6 space-y-4">
          {attraction.article.map((para, i) => (
            <p key={i} className="text-brand-ocean text-sm leading-relaxed">{para}</p>
          ))}
        </div>

        {/* CTA */}
        <div className="px-6 pb-6">
          <div className="border-t border-brand-sand pt-5">
            <p className="text-xs text-brand-ocean mb-3 text-center">
              Ready to visit? Book your Zanzibar airport transfer with us.
            </p>
            <a
              href={`https://wa.me/${WHATSAPP_NUM}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#128C7E] transition-colors"
            >
              {WA_ICON} Enquire on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Success modal ──────────────────────────────────────────────────
function SuccessModal({ bookingRef, zone, hotel, direction, vehicle, price, onClose }) {
  const [cashState, setCashState] = useState('idle'); // idle | loading | done | error
  const route = direction === 'airport_to_hotel'
    ? `${AIRPORT_LABEL} → ${hotel}, ${zone}`
    : `${hotel}, ${zone} → ${AIRPORT_LABEL}`;

  const confirmCash = async () => {
    if (!bookingRef || cashState === 'loading') return;
    setCashState('loading');
    try {
      await api.post('/api/bookings/confirm-cash', { reference: bookingRef });
      setCashState('done');
    } catch {
      setCashState('error');
    }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto animate-modal-in">
        <div className="p-8">
          <div className="flex justify-center mb-5">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="font-serif text-2xl font-bold text-brand-green text-center mb-1">Booking Received!</h2>
          {bookingRef && <p className="text-center text-sm text-brand-ocean mb-2">Ref: <strong className="text-brand-green">{bookingRef}</strong></p>}
          <div className="bg-brand-bg rounded-xl p-3 text-center mb-4 border border-brand-sand">
            <p className="text-brand-green text-xs font-semibold leading-snug">{route}</p>
            {vehicle && price && <p className="text-brand-gold font-bold mt-1">{vehicle} · ${price} per vehicle</p>}
          </div>
          <p className="text-center text-gray-500 text-sm mb-5">Complete payment below to secure your booking.</p>
          <div className="space-y-3 mb-5">
            {/* CRDB Bank */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 bg-blue-700 rounded-md flex items-center justify-center"><span className="text-white font-bold text-xs">CR</span></div>
                <span className="font-bold text-blue-800 text-sm">Bank Transfer · CRDB Bank</span>
              </div>
              <p className="text-xs text-gray-500 mb-0.5">Account Number:</p>
              <p className="font-mono font-bold text-blue-900 text-xl tracking-wider">{CRDB_ACCOUNT}</p>
              <p className="text-xs text-gray-500 mt-0.5">Name: <span className="font-semibold text-blue-900">{BANK_HOLDER}</span></p>
            </div>
            {/* NMB Bank */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 bg-indigo-700 rounded-md flex items-center justify-center"><span className="text-white font-bold text-xs">NM</span></div>
                <span className="font-bold text-indigo-800 text-sm">Bank Transfer · NMB Bank</span>
              </div>
              <p className="text-xs text-gray-500 mb-0.5">Account Number:</p>
              <p className="font-mono font-bold text-indigo-900 text-xl tracking-wider">{NMB_ACCOUNT}</p>
              <p className="text-xs text-gray-500 mt-0.5">Name: <span className="font-semibold text-indigo-900">{BANK_HOLDER}</span></p>
            </div>
            {/* Mobile Money */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 bg-[#00A651] rounded-md flex items-center justify-center"><span className="text-white font-bold text-xs">M</span></div>
                <span className="font-bold text-green-700 text-sm">M-Pesa / Tigo Pesa / Airtel Money</span>
              </div>
              <p className="text-xs text-gray-500 mb-0.5">Number:</p>
              <p className="font-mono font-bold text-green-800 text-xl">{MPESA_TILL}</p>
              <p className="text-xs text-gray-400 mt-0.5">Name: Neruda Tours &amp; Safaris</p>
            </div>
            {/* Cash on Arrival — self-confirm */}
            {cashState === 'done' ? (
              <div className="bg-green-50 border-2 border-green-400 rounded-xl p-5 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-green-800 font-bold text-base mb-1">Booking Confirmed!</p>
                <p className="text-green-700 text-sm">Check your email — your booking confirmation has been sent. Show it to the driver on the day.</p>
              </div>
            ) : (
              <div className="border-2 border-amber-400 rounded-xl overflow-hidden">
                <div className="bg-amber-50 px-4 pt-3 pb-2">
                  <p className="text-amber-800 font-bold text-sm">💵 Paying Cash on Arrival?</p>
                  <p className="text-amber-700 text-xs mt-0.5">Click below to confirm your spot and receive your booking confirmation by email.</p>
                </div>
                <button
                  onClick={confirmCash}
                  disabled={cashState === 'loading' || !bookingRef}
                  className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-colors disabled:opacity-60"
                >
                  {cashState === 'loading' ? 'Confirming…' : 'Confirm Cash on Arrival — Get Ticket Now'}
                </button>
              </div>
            )}
          </div>
          <a href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] text-white font-semibold rounded-xl hover:bg-[#128C7E] transition-colors text-sm mb-3">
            {WA_ICON} Confirm on WhatsApp: {WHATSAPP_DISPLAY}
          </a>
          <button onClick={onClose}
            className="w-full py-3 border-2 border-brand-sand text-brand-ocean font-semibold rounded-xl hover:border-brand-green hover:text-brand-green transition-colors text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Form CSS classes ───────────────────────────────────────────────
const iCls = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green text-brand-green placeholder-gray-400 transition-colors text-sm';
const lCls = 'block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide';
const dCls = 'w-full px-3 py-2.5 rounded-lg border border-gray-100 bg-gray-50 text-gray-400 text-sm cursor-not-allowed select-none';

// ══════════════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════════════
export default function HomePage() {
  // ── data ────────────────────────────────────────────────────────
  // Start immediately with fallback so prices are visible even before API responds
  const [destinations, setDestinations] = useState(PRICES_FALLBACK);
  const [apiLoaded, setApiLoaded]       = useState(false);

  // ── form ────────────────────────────────────────────────────────
  const [direction, setDirection] = useState('airport_to_hotel');
  const [form, setForm] = useState({
    pricing_zone:'', hotel_name:'', vehicle_type:'', vehicle_price:null,
    pickup_date:'', pickup_time:'', passengers:'1',
    customer_name:'', customer_phone:'', customer_email:'', special_requests:'',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [activeAttraction, setActiveAttraction] = useState(null);
  const [modalData, setModalData]   = useState({});

  // ── faq ─────────────────────────────────────────────────────────
  const [openFaq, setOpenFaq] = useState(null);

  // ── scroll reveal ────────────────────────────────────────────────
  const [vRef, vVis] = useReveal();
  const [zRef, zVis] = useReveal();
  const [tRef, tVis] = useReveal();
  const [rRef, rVis] = useReveal();
  const [fRef, fVis] = useReveal();

  // ── load live prices (replace fallback if API works) ────────────
  useEffect(() => {
    console.log('[Neruda] API base:', api.defaults.baseURL);
    api.get('/api/prices')
      .then(res => {
        console.log('[Neruda] Live prices loaded:', res.data?.length, 'destinations');
        if (Array.isArray(res.data) && res.data.length > 0) {
          setDestinations(res.data);
          setApiLoaded(true);
        }
      })
      .catch(err => {
        console.warn('[Neruda] API unreachable — showing built-in prices. Error:', err.message);
        // destinations already set to PRICES_FALLBACK — nothing to do
      });
  }, []);

  // ── derived ─────────────────────────────────────────────────────
  const selectedDest    = destinations.find(d => d.destination === form.pricing_zone);
  const selectedVehicle = selectedDest?.vehicles?.find(v => v.vehicle_type === form.vehicle_type);
  const maxPax          = selectedVehicle?.max_passengers ?? 20;
  const hotels          = HOTELS_BY_ZONE[form.pricing_zone] || [];

  const isComplete = !!(form.pricing_zone && form.hotel_name.trim() && form.vehicle_type
    && form.pickup_date && form.pickup_time && form.customer_name.trim() && form.customer_phone.trim()
    && form.customer_email.trim());

  const alphardRows = destinations.map(d => ({ dest:d.destination, price:d.vehicles?.find(v=>v.vehicle_type==='alphard')?.price_usd })).filter(r=>r.price!=null);
  const minivanRows = destinations.map(d => ({ dest:d.destination, price:d.vehicles?.find(v=>v.vehicle_type==='minivan')?.price_usd })).filter(r=>r.price!=null);

  // ── handlers ────────────────────────────────────────────────────
  const setDir = val => {
    setDirection(val);
    setForm(p => ({ ...p, pricing_zone:'', hotel_name:'', vehicle_type:'', vehicle_price:null, passengers:'1' }));
    setFormError('');
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => {
      const n = { ...p, [name]:value };
      if (name === 'pricing_zone') { n.hotel_name=''; n.vehicle_type=''; n.vehicle_price=null; n.passengers='1'; }
      return n;
    });
    setFormError('');
  };

  const selectVehicle = v => {
    setForm(p => ({ ...p, vehicle_type:v.vehicle_type, vehicle_price:v.price_usd, passengers:'1' }));
    setFormError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isComplete) { setFormError('Please fill in all required fields.'); return; }
    setSubmitting(true); setFormError('');
    try {
      const pickup  = direction==='airport_to_hotel' ? AIRPORT_LABEL : `${form.hotel_name}, ${form.pricing_zone}`;
      const dropoff = direction==='airport_to_hotel' ? `${form.hotel_name}, ${form.pricing_zone}` : AIRPORT_LABEL;
      const payload = {
        customer_name:form.customer_name.trim(), customer_phone:form.customer_phone.trim(),
        pickup_location:pickup, dropoff_location:dropoff, pricing_zone:form.pricing_zone,
        vehicle_type:form.vehicle_type, vehicle_price:form.vehicle_price,
        pickup_date:form.pickup_date, pickup_time:form.pickup_time,
        passengers:parseInt(form.passengers, 10),
        ...(form.customer_email?.trim()   && { customer_email:form.customer_email.trim() }),
        ...(form.special_requests?.trim() && { special_requests:form.special_requests.trim() }),
      };
      const res = await api.post('/api/bookings', payload);
      setModalData({ bookingRef:res.data.booking_reference||'', zone:form.pricing_zone,
        hotel:form.hotel_name, direction, vehicle:selectedVehicle?.vehicle_label||'', price:form.vehicle_price });
      setShowModal(true);
      setDirection('airport_to_hotel');
      setForm({ pricing_zone:'', hotel_name:'', vehicle_type:'', vehicle_price:null,
        pickup_date:'', pickup_time:'', passengers:'1', customer_name:'', customer_phone:'',
        customer_email:'', special_requests:'' });
    } catch (err) {
      const serverMsg = err.response?.data?.error || err.response?.data?.message || '';
      if (!err.response || err.response.status >= 500) {
        setFormError('Our server is temporarily unavailable. Please book via WhatsApp: +255 779 987 604');
      } else {
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } finally { setSubmitting(false); }
  };

  // ════════════════════════════════════════════════════════════════
  return (
    <div className="bg-brand-bg font-sans text-brand-green">
      <Navbar />

      {/* ══════════════════════════════════════════════════════════
          HERO  ·  Zanzibar beach background
      ══════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-x-hidden">

        {/* Background — tropical Indian Ocean beach */}
        <div className="absolute inset-0 hero-bg" />

        {/* Dark green overlay 60% */}
        <div className="absolute inset-0 bg-[#1A3A2A]" style={{ opacity:0.62 }} />

        {/* Subtle diagonal grid texture */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage:'repeating-linear-gradient(60deg,#C4956A 0px,transparent 1px,transparent 60px,#C4956A 61px)' }} />

        <TransferRouteAnimation />

        {/* ── Floating decorative icons (z-1, well clear of text) ─ */}
        <div className="absolute top-1/3 right-10 animate-float-2 opacity-[0.18] pointer-events-none hidden md:block" style={{ zIndex:1 }}>
          <svg className="w-14 h-14 text-brand-sand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
          </svg>
        </div>
        <div className="absolute bottom-32 left-12 animate-float-3 opacity-[0.13] pointer-events-none hidden md:block" style={{ zIndex:1 }}>
          <svg className="w-9 h-9 text-brand-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <div className="absolute bottom-1/3 right-16 animate-float opacity-[0.10] pointer-events-none hidden lg:block" style={{ zIndex:1 }}>
          <svg className="w-8 h-8 text-brand-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </div>

        {/* ── Hero content ────────────────────────────────────── */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-14 items-start">

            {/* Left — headline */}
            <div className="text-white pt-4">
              <p className="animate-hero-text text-brand-gold text-xs font-bold tracking-widest uppercase mb-5">
                Zanzibar · Tanzania
              </p>
              <h1 className="animate-hero-text-delay font-serif text-4xl sm:text-5xl xl:text-6xl font-bold leading-tight mb-5">
                Book Your<br />Zanzibar Transfer
              </h1>
              <p className="animate-hero-text-delay-2 text-white/80 text-lg xl:text-xl mb-8 leading-relaxed max-w-md">
                Reliable private airport transfers across the island
              </p>

              <div className="animate-hero-text-delay-2 flex flex-wrap gap-3 mb-10">
                {[
                  { label:'Available 24/7',      d:'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { label:'Professional Drivers', d:'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
                  { label:'Price Per Vehicle',    d:'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
                ].map(({ label, d }) => (
                  <div key={label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                    <svg className="w-4 h-4 text-brand-gold shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
                    </svg>
                    <span className="text-white text-sm font-semibold">{label}</span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 border-t border-white/20 pt-8 max-w-xs">
                {[{ n:'3+', l:'Years' }, { n:'2K+', l:'Transfers' }, { n:'9', l:'Zones' }].map(({ n, l }) => (
                  <div key={l}>
                    <p className="font-serif text-2xl font-bold text-brand-gold">{n}</p>
                    <p className="text-brand-sand text-xs mt-0.5">{l}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — booking form */}
            <TransferRouteAnimation variant="mobile" />

            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-brand-green px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-serif text-lg font-bold text-white">Book Your Transfer</h2>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${
                    apiLoaded ? 'bg-brand-gold text-brand-green' : 'bg-white/10 text-brand-sand'
                  }`}>
                    {apiLoaded ? 'Live prices' : 'Loading prices'}
                  </span>
                </div>
                <p className="text-brand-sand/80 text-xs mt-0.5">Price per vehicle · not per person</p>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4">

                {/* Direction */}
                <div>
                  <label className={lCls}>Transfer Direction *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[['airport_to_hotel','✈  Airport → Hotel'],['hotel_to_airport','🏨  Hotel → Airport']].map(([val,label]) => (
                      <button key={val} type="button" onClick={() => setDir(val)}
                        className={`py-2.5 px-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                          direction===val ? 'border-brand-green bg-brand-green text-white' : 'border-gray-200 text-brand-ocean hover:border-brand-gold'}`}>
                        {val === 'airport_to_hotel' ? 'Airport to Hotel' : 'Hotel to Airport'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Airport→Hotel: fixed pickup */}
                {direction==='airport_to_hotel' && (
                  <div>
                    <label className={lCls}>Pickup From</label>
                    <div className={dCls}>{AIRPORT_LABEL}</div>
                  </div>
                )}

                {/* Hotel→Airport: zone first */}
                {direction==='hotel_to_airport' && (
                  <div>
                    <label className={lCls}>Pickup Zone *</label>
                    <select name="pricing_zone" value={form.pricing_zone} onChange={handleChange} required className={iCls}>
                      <option value="">Select your hotel zone</option>
                      {destinations.map(d => <option key={d.destination} value={d.destination}>{d.destination}</option>)}
                    </select>
                  </div>
                )}

                {/* Hotel→Airport: hotel name after zone */}
                {direction==='hotel_to_airport' && form.pricing_zone && (
                  <div>
                    <label className={lCls}>Your Hotel Name *</label>
                    <input list="h-list" name="hotel_name" value={form.hotel_name} onChange={handleChange}
                      required placeholder={`Hotel in ${form.pricing_zone}`} className={iCls} autoComplete="off" />
                    <datalist id="h-list">{hotels.map(h => <option key={h} value={h} />)}</datalist>
                  </div>
                )}

                {/* Airport→Hotel: destination zone */}
                {direction==='airport_to_hotel' && (
                  <div>
                    <label className={lCls}>Destination Zone *</label>
                    <select name="pricing_zone" value={form.pricing_zone} onChange={handleChange} required className={iCls}>
                      <option value="">Select destination area</option>
                      {destinations.map(d => <option key={d.destination} value={d.destination}>{d.destination}</option>)}
                    </select>
                  </div>
                )}

                {/* Airport→Hotel: hotel name after zone */}
                {direction==='airport_to_hotel' && form.pricing_zone && (
                  <div>
                    <label className={lCls}>Hotel Name in {form.pricing_zone} *</label>
                    <input list="h-list" name="hotel_name" value={form.hotel_name} onChange={handleChange}
                      required placeholder={`Hotel in ${form.pricing_zone}`} className={iCls} autoComplete="off" />
                    <datalist id="h-list">{hotels.map(h => <option key={h} value={h} />)}</datalist>
                  </div>
                )}

                {/* Hotel→Airport: fixed dropoff */}
                {direction==='hotel_to_airport' && (
                  <div>
                    <label className={lCls}>Drop-off To</label>
                    <div className={dCls}>{AIRPORT_LABEL}</div>
                  </div>
                )}

                {/* Vehicle cards */}
                {selectedDest && (
                  <div>
                    <label className={lCls}>Vehicle Type * <span className="font-normal normal-case text-gray-400">— price per vehicle</span></label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                      {selectedDest.vehicles.map(v => {
                        const sel = form.vehicle_type===v.vehicle_type;
                        return (
                          <button key={v.vehicle_type} type="button" onClick={() => selectVehicle(v)}
                            className={`relative rounded-xl border-2 p-3.5 text-left transition-all duration-200 ${
                              sel ? 'border-brand-green bg-brand-green/5 shadow-md scale-[1.02]' : 'border-gray-200 hover:border-brand-gold hover:shadow-sm'}`}>
                            {sel && (
                              <span className="absolute top-2 right-2 w-5 h-5 bg-brand-green rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </span>
                            )}
                            <p className="font-bold text-brand-green text-sm pr-5">{v.vehicle_label}</p>
                            <p className="text-brand-ocean text-xs mt-0.5">Max {v.max_passengers} pax</p>
                            <p className="text-brand-gold font-bold text-xl mt-2">${v.price_usd}</p>
                            <p className="text-gray-400 text-xs">per vehicle</p>
                          </button>
                        );
                      })}
                    </div>
                    {selectedVehicle && (
                      <div className="mt-3 flex items-center justify-between bg-brand-green/5 border border-brand-green/20 rounded-lg px-4 py-3">
                        <span className="text-brand-ocean text-sm">Total</span>
                        <span className="text-brand-green font-bold text-xl">
                          ${selectedVehicle.price_usd} USD
                          <span className="text-xs font-normal text-brand-ocean ml-1">· per vehicle</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Date / Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={lCls}>Pickup Date *</label>
                    <input name="pickup_date" type="date" value={form.pickup_date} onChange={handleChange}
                      required min={new Date().toISOString().split('T')[0]} className={iCls} />
                  </div>
                  <div>
                    <label className={lCls}>Pickup Time *</label>
                    <input name="pickup_time" type="time" value={form.pickup_time} onChange={handleChange} required className={iCls} />
                  </div>
                </div>

                {/* Passengers */}
                <div>
                  <label className={lCls}>Passengers *{selectedVehicle && <span className="font-normal normal-case text-gray-400 ml-1">(max {maxPax})</span>}</label>
                  <select name="passengers" value={form.passengers} onChange={handleChange} required className={iCls}>
                    {Array.from({ length:maxPax }, (_,i) => i+1).map(n => (
                      <option key={n} value={n}>{n} {n===1?'Passenger':'Passengers'}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={lCls}>Full Name *</label>
                  <input name="customer_name" value={form.customer_name} onChange={handleChange} required placeholder="Your full name" className={iCls} />
                </div>
                <div>
                  <label className={lCls}>Phone Number *</label>
                  <input name="customer_phone" value={form.customer_phone} onChange={handleChange} required placeholder="+255 ..." className={iCls} />
                </div>
                <div>
                  <label className={lCls}>Email *</label>
                  <input name="customer_email" type="email" value={form.customer_email} onChange={handleChange} required placeholder="For booking confirmation" className={iCls} />
                </div>
                <div>
                  <label className={lCls}>Special Requests <span className="font-normal normal-case text-gray-400">(optional)</span></label>
                  <textarea name="special_requests" value={form.special_requests} onChange={handleChange} rows={2}
                    placeholder="Flight number, child seats, extra luggage…" className={`${iCls} resize-none`} />
                </div>

                {formError && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2.5 rounded-lg">{formError}</p>}

                <button type="submit" disabled={submitting||!isComplete}
                  className="w-full py-4 bg-brand-green text-white font-bold text-base rounded-xl flex items-center justify-center gap-2 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-brand-ocean enabled:hover:-translate-y-0.5 enabled:hover:shadow-lg">
                  {submitting
                    ? <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing…</>
                    : <>Book Now <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg></>}
                </button>
                {!isComplete && !submitting && <p className="text-center text-gray-400 text-xs -mt-1">Fill in all required fields to enable booking</p>}
              </form>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
          <svg className="w-6 h-6 text-brand-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* ── Stat strip ─────────────────────────────────────────── */}
      <StatStrip />

      {/* ── Destinations ticker ────────────────────────────────── */}
      <div className="bg-brand-green py-3 overflow-hidden border-y border-brand-ocean/60">
        <div className="animate-marquee">
          {TICKER.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-3 mx-4 text-brand-gold text-xs font-bold tracking-widest uppercase">
              {t}<span className="text-brand-sand/40 text-base">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          VEHICLES WITH PRICE TABLES
      ══════════════════════════════════════════════════════════ */}
      <section id="vehicles" ref={vRef}
        className={`py-20 px-6 bg-white reveal ${vVis ? 'visible' : ''}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <p className="text-brand-gold text-xs font-bold tracking-widest uppercase mb-3">Our Fleet</p>
            <h2 className="font-serif text-4xl font-bold text-brand-green mb-3">Choose Your Vehicle</h2>
            <p className="text-brand-ocean text-sm mb-1">
              All prices are <strong>per vehicle</strong>, not per person · one-way from / to Zanzibar Airport
            </p>
            <div className="w-14 h-1 bg-brand-gold mx-auto mt-5 rounded-full" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">

            {/* Alphard */}
            <div className="group bg-white border border-brand-sand rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col">
              <div className="relative h-56 overflow-hidden">
                <img src={VEHICLE_IMAGES.alphard}
                  alt="Toyota Alphard VIP"
                  width="500"
                  height="224"
                  loading="lazy"
                  style={{ backgroundColor: '#1A3A2A', objectFit: 'cover' }}
                  onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 card-img" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div>
                    <h3 className="font-serif text-xl font-bold text-white">Toyota Alphard VIP</h3>
                    <p className="text-brand-sand text-xs mt-0.5">Premium Luxury MPV</p>
                  </div>
                  <span className="bg-brand-gold text-brand-green text-xs font-bold px-3 py-1 rounded-full">Up to 5 Pax</span>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-brand-ocean text-sm leading-relaxed mb-5">
                  Premium luxury MPV perfect for small groups and VIP transfers — leather seats, air conditioning, and ample luggage space.
                </p>
                <div className="flex-1">
                  {alphardRows.map(({ dest, price }) => (
                    <div key={dest}
                      className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm mb-1 transition-all duration-200 ${
                        form.pricing_zone===dest
                          ? 'bg-brand-green/10 border border-brand-green/30 scale-[1.02]'
                          : 'hover:bg-brand-bg'}`}>
                      <span className={`font-medium ${form.pricing_zone===dest ? 'text-brand-green font-bold' : 'text-brand-green'}`}>{dest}</span>
                      <span className={`font-bold ${form.pricing_zone===dest ? 'text-brand-green text-base' : 'text-brand-gold'}`}>${price}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mt-4 text-center border-t border-brand-sand/40 pt-3">
                  * Price is per vehicle, not per person
                </p>
              </div>
            </div>

            {/* Mini Van */}
            <div className="group bg-white border border-brand-sand rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col">
              <div className="relative h-56 overflow-hidden">
                <img src={VEHICLE_IMAGES.minivan}
                  alt="Toyota Mini Van"
                  width="500"
                  height="224"
                  loading="lazy"
                  style={{ backgroundColor: '#1A3A2A', objectFit: 'cover' }}
                  onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 card-img" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div>
                    <h3 className="font-serif text-xl font-bold text-white">Toyota Mini Van</h3>
                    <p className="text-brand-sand text-xs mt-0.5">Group Transfer</p>
                  </div>
                  <span className="bg-brand-gold text-brand-green text-xs font-bold px-3 py-1 rounded-full">Up to 10 Pax</span>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-brand-ocean text-sm leading-relaxed mb-5">
                  Comfortable spacious van ideal for families and medium groups — ample luggage space, air conditioning, and comfortable seating.
                </p>
                <div className="flex-1">
                  {minivanRows.map(({ dest, price }) => (
                    <div key={dest}
                      className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm mb-1 transition-all duration-200 ${
                        form.pricing_zone===dest
                          ? 'bg-brand-green/10 border border-brand-green/30 scale-[1.02]'
                          : 'hover:bg-brand-bg'}`}>
                      <span className={`font-medium ${form.pricing_zone===dest ? 'text-brand-green font-bold' : 'text-brand-green'}`}>{dest}</span>
                      <span className={`font-bold ${form.pricing_zone===dest ? 'text-brand-green text-base' : 'text-brand-gold'}`}>${price}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mt-4 text-center border-t border-brand-sand/40 pt-3">
                  * Price is per vehicle, not per person
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          INCLUDED IN EVERY TRANSFER
      ══════════════════════════════════════════════════════════ */}
      <section className="py-10 px-6 bg-brand-green">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-brand-gold text-xs font-bold tracking-widest uppercase mb-6">Every Transfer Includes</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon:'❄️', label:'Full Air Condition' },
              { icon:'📶', label:'Free WiFi' },
              { icon:'🍶', label:'Water Bottle' },
              { icon:'👶🪑', label:'Baby Booster Seat' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-2 bg-white/10 border border-white/20 rounded-2xl px-4 py-5 text-center">
                <span className="text-3xl">{icon}</span>
                <span className="text-white text-sm font-semibold leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          EXPLORE ZANZIBAR
      ══════════════════════════════════════════════════════════ */}
      <section id="zanzibar" ref={zRef}
        className={`py-20 px-6 bg-brand-bg reveal ${zVis ? 'visible' : ''}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-brand-gold text-xs font-bold tracking-widest uppercase mb-3">Island Activities</p>
            <h2 className="font-serif text-4xl font-bold text-brand-green mb-3">Explore Zanzibar</h2>
            <div className="w-14 h-1 bg-brand-gold mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ZANZIBAR_ACTIVITIES.map((a, i) => (
              <div key={a.title}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 hover:-translate-y-1.5 flex flex-col"
                style={{ transitionDelay:`${i*60}ms` }}>
                <div className="relative h-48 overflow-hidden">
                  <img src={a.img} alt={a.title} loading="lazy"
                    width="400"
                    height="192"
                    style={{ backgroundColor: '#1A3A2A', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 card-img" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <h3 className="absolute bottom-3 left-4 right-4 font-serif text-sm font-bold text-white leading-tight">{a.title}</h3>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <p className="text-brand-ocean text-sm leading-relaxed flex-1">{a.desc}</p>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => setActiveAttraction(a)}
                      className="flex-1 py-2.5 border-2 border-brand-green text-brand-green text-sm font-bold rounded-xl hover:bg-brand-green hover:text-white transition-colors"
                    >
                      Read More
                    </button>
                    <a href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#128C7E] transition-colors">
                      {WA_ICON} Enquire
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          EXPLORE TANZANIA
      ══════════════════════════════════════════════════════════ */}
      <section id="tanzania" ref={tRef}
        className={`py-20 px-6 bg-brand-green reveal ${tVis ? 'visible' : ''}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-brand-gold text-xs font-bold tracking-widest uppercase mb-3">Safari &amp; Wildlife</p>
            <h2 className="font-serif text-4xl font-bold text-white mb-3">Explore Tanzania</h2>
            <div className="w-14 h-1 bg-brand-gold mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TANZANIA_SAFARIS.map((s, i) => (
              <div key={s.title}
                className="group rounded-2xl overflow-hidden bg-white/10 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1.5 flex flex-col"
                style={{ transitionDelay:`${i*80}ms` }}>
                <div className="relative h-44 overflow-hidden">
                  <img src={s.img} alt={s.title} loading="lazy"
                    width="300"
                    height="176"
                    style={{ backgroundColor: '#1A3A2A', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 card-img" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <h3 className="absolute bottom-3 left-3 right-3 font-serif text-sm font-bold text-white leading-tight">{s.title}</h3>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <p className="text-brand-sand/80 text-xs leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => setActiveAttraction(s)}
                      className="flex-1 py-2 border border-brand-gold text-brand-gold text-xs font-bold rounded-lg hover:bg-brand-gold hover:text-brand-green transition-colors"
                    >
                      Read More
                    </button>
                    <a href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
                      className="flex-1 flex items-center justify-center gap-1 py-2 bg-brand-gold text-brand-green text-xs font-bold rounded-lg hover:bg-brand-sand transition-colors">
                      {WA_ICON} Enquire
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════════════════ */}
      <section ref={rRef} className={`py-20 px-6 bg-white reveal ${rVis ? 'visible' : ''}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-brand-gold text-xs font-bold tracking-widest uppercase mb-3">Happy Travellers</p>
            <h2 className="font-serif text-4xl font-bold text-brand-green mb-3">What Our Guests Say</h2>
            <div className="w-14 h-1 bg-brand-gold mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={t.name}
                className={`bg-brand-bg border border-brand-sand rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300${
                  i === TESTIMONIALS.length - 1 && TESTIMONIALS.length % 4 !== 0
                    ? ' sm:col-span-2 lg:col-span-1 xl:col-span-1'
                    : ''
                }`}
                style={{ transitionDelay:`${i*70}ms` }}>
                <Stars n={t.stars} />
                <p className="text-brand-green text-sm leading-relaxed mb-5 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-2 border-t border-brand-sand pt-3">
                  <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-xs">{t.name[0]}</span>
                  </div>
                  <span className="font-semibold text-brand-green text-sm">{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          FAQ
      ══════════════════════════════════════════════════════════ */}
      <section ref={fRef} className={`py-20 px-6 bg-brand-bg reveal ${fVis ? 'visible' : ''}`}>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-brand-gold text-xs font-bold tracking-widest uppercase mb-3">Questions</p>
            <h2 className="font-serif text-4xl font-bold text-brand-green mb-3">Frequently Asked</h2>
            <div className="w-14 h-1 bg-brand-gold mx-auto rounded-full" />
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} open={openFaq===i} onToggle={() => setOpenFaq(openFaq===i ? null : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          FOOTER / CONTACT
      ══════════════════════════════════════════════════════════ */}
      <footer id="contact" className="bg-brand-green py-14 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-xl px-5 py-2.5 shadow-lg inline-block">
              <img
                src={`${process.env.PUBLIC_URL}/neruda_logo_hq.webp`}
                alt="Neruda Tours & Safaris"
                width="120"
                height="40"
                loading="lazy"
                className="h-14 w-auto object-contain"
              />
            </div>
          </div>
          <p className="text-brand-sand text-sm mb-6 tracking-wider">Private Transfers across Zanzibar</p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <a href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors">
              {WA_ICON} {WHATSAPP_DISPLAY}
            </a>
            <a href="https://instagram.com/neruda_tours_and_safaris" target="_blank" rel="noreferrer"
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              @neruda_tours_and_safaris
            </a>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-brand-ocean text-xs">&copy; 2026 Neruda Tours and Safaris · Zanzibar, Tanzania · All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ── Floating WhatsApp button ──────────────────────────── */}
      <a href={`https://wa.me/${WHATSAPP_NUM}`} target="_blank" rel="noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-xl hover:bg-[#128C7E] hover:scale-110 transition-all animate-bounce-slow">
        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      {showModal && <SuccessModal {...modalData} onClose={() => setShowModal(false)} />}
      <AttractionModal attraction={activeAttraction} onClose={() => setActiveAttraction(null)} />
    </div>
  );
}
